import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from 'react-bootstrap';
import Api from '../../Auth/Api';
import ModalCargo from './ModalCargo';
import DropdownMenu from '../DropdownMenu';
import { customStyles } from '../../utils/selectStyles';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const CargosManager = ({ token, onToast }) => {
	const [dataCargos, setDataCargos] = useState([]);
	const [dataCursos, setDataCursos] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadingSave, setLoadingSave] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [cargoEditando, setCargoEditando] = useState(null);
	const [showModalDelete, setShowModalDelete] = useState(false);
	const [cargoParaDeletar, setCargoParaDeletar] = useState(null);
	const [loadingDelete, setLoadingDelete] = useState(false);

	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [refreshKey, setRefreshKey] = useState(0);

	const onToastRef = useRef(onToast);
	onToastRef.current = onToast;

	const {
		register,
		handleSubmit,
		control,
		reset,
		setValue,
		formState: { errors }
	} = useForm();

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
			setPage(1);
		}, 400);

		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		let cancelled = false;

		const loadCargos = async () => {
			setLoading(true);
			try {
				const response = await Api.get('api/cargos', {
					headers: { Authorization: `Bearer ${token}` },
					params: {
						search: debouncedSearch,
						page,
						limit: pageSize
					}
				});

				if (cancelled) return;

				const { data, total: totalItems, totalPages: pages, page: currentPage } = response.data;

				if ((data || []).length === 0 && page > 1 && pages > 0) {
					setPage(pages);
					return;
				}

				setDataCargos(data || []);
				setTotal(totalItems || 0);
				setTotalPages(pages || 0);

				const nextPage = currentPage || page;
				setPage((prev) => (prev !== nextPage ? nextPage : prev));
			} catch (error) {
				if (cancelled) return;
				console.error('Erro ao carregar cargos:', error);
				onToastRef.current?.('Erro!', error.response?.data?.message || 'Não foi possível carregar os cargos.', 'danger');
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		loadCargos();

		return () => {
			cancelled = true;
		};
	}, [token, debouncedSearch, page, pageSize, refreshKey]);

	useEffect(() => {
		const fetchCursos = async () => {
			try {
				const response = await Api.get('api/listar-cursos', {
					headers: { Authorization: `Bearer ${token}` }
				});
				setDataCursos(response.data);
			} catch (error) {
				console.error('Erro ao carregar cursos:', error);
			}
		};

		fetchCursos();
	}, [token]);

	const handleOpenCreate = () => {
		setCargoEditando(null);
		reset({ nome: '', cursos: [] });
		setShowModal(true);
	};

	const handleOpenEdit = (cargo) => {
		setCargoEditando(cargo);
		setValue('nome', cargo.nome);
		setValue('cursos', (cargo.cursos || []).map((curso) => ({
			value: curso.id,
			label: curso.nome
		})));
		setShowModal(true);
	};

	const handleOpenDelete = (cargo) => {
		setCargoParaDeletar(cargo);
		setShowModalDelete(true);
	};

	const onSubmit = async (data) => {
		setLoadingSave(true);
		const payload = {
			nome: data.nome,
			cursos: (data.cursos || []).map((curso) => curso.value)
		};

		try {
			if (cargoEditando) {
				await Api.post(`api/editar-cargo/${cargoEditando.id}`, payload, {
					headers: { Authorization: `Bearer ${token}` }
				});
				onToast?.('Sucesso!', 'Cargo atualizado com sucesso.', 'success');
			} else {
				await Api.post('api/cargo', payload, {
					headers: { Authorization: `Bearer ${token}` }
				});
				onToast?.('Sucesso!', 'Cargo criado com sucesso.', 'success');
				setPage(1);
			}

			setShowModal(false);
			reset({ nome: '', cursos: [] });
			setRefreshKey((prev) => prev + 1);
		} catch (error) {
			onToast?.('Erro!', error.response?.data?.message || 'Erro ao salvar cargo.', 'danger');
		} finally {
			setLoadingSave(false);
		}
	};

	const handleDelete = async () => {
		if (!cargoParaDeletar) return;
		setLoadingDelete(true);

		try {
			await Api.delete(`api/deletar-cargo/${cargoParaDeletar.id}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			onToast?.('Sucesso!', 'Cargo excluído com sucesso.', 'success');
			setShowModalDelete(false);
			setCargoParaDeletar(null);
			setRefreshKey((prev) => prev + 1);
		} catch (error) {
			onToast?.('Erro!', error.response?.data?.message || 'Erro ao excluir cargo.', 'danger');
			setShowModalDelete(false);
		} finally {
			setLoadingDelete(false);
		}
	};

	const canPreviousPage = page > 1;
	const canNextPage = page < totalPages;
	const exibindoDe = total === 0 ? 0 : (page - 1) * pageSize + 1;
	const exibindoAte = Math.min(page * pageSize, total);

	return (
		<>
			<div className="d-flex justify-content-between align-items-center mb-4 mt-4">
				<div>
					<h5 className="mb-1 fw-semibold text-primary">Gerenciar Cargos</h5>
					<p className="text-muted mb-0 fs-7">Cadastre cargos e vincule cursos recomendados para novos alunos.</p>
				</div>
				<button type="button" className="btn btn-primary" onClick={handleOpenCreate}>
					<i className="bi bi-plus-lg me-2"></i>
					Novo cargo
				</button>
			</div>

			<div className="card border-0 shadow-sm mb-3">
				<div className="card-body p-3">
					<div className="row g-2 align-items-end">
						<div className="col-md-8">
							<label className="form-label text-primary fw-medium small mb-1">Pesquisar cargo</label>
							<div className="inner-addon right-addon">
								<i className="d-flex bi bi-search" />
								<input
									type="search"
									className="form-control py-2"
									placeholder="Pesquise pelo nome do cargo..."
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</div>
						</div>
						<div className="col-md-4">
							<label className="form-label text-primary fw-medium small mb-1">Registros por página</label>
							<select
								className="form-select py-2"
								value={pageSize}
								onChange={(e) => {
									setPageSize(Number(e.target.value));
									setPage(1);
								}}
							>
								{PAGE_SIZE_OPTIONS.map((size) => (
									<option key={size} value={size}>{size}</option>
								))}
							</select>
						</div>
					</div>
				</div>
			</div>

			{loading ? (
				<div className="d-flex justify-content-center my-5">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Carregando...</span>
					</div>
				</div>
			) : (
				<div className="table-responsive">
					<table className="table table-hover align-middle">
						<thead className="table-light">
							<tr>
								<th>Nome</th>
								<th className="text-center">Cursos</th>
								<th className="text-center">Usuários</th>
								<th className="text-end">Ações</th>
							</tr>
						</thead>
						<tbody>
							{dataCargos.length === 0 ? (
								<tr>
									<td colSpan="4" className="text-center text-muted py-4">
										{debouncedSearch ? 'Nenhum cargo encontrado para a pesquisa.' : 'Nenhum cargo cadastrado.'}
									</td>
								</tr>
							) : (
								dataCargos.map((cargo) => (
									<tr key={cargo.id}>
										<td className="fw-medium">{cargo.nome}</td>
										<td className="text-center">
											<span className="badge bg-info">{cargo.cursos?.length || 0}</span>
										</td>
										<td className="text-center">
											<span className="badge bg-secondary">{cargo.qtd_usuarios || 0}</span>
										</td>
										<td className="text-end">
											<div style={{ position: 'static' }} className="d-flex align-items-center justify-content-end">
												<DropdownMenu
													items={[
														{
															icon: 'pencil',
															label: 'Editar cargo',
															onClick: () => handleOpenEdit(cargo)
														},
														{ divider: true },
														{
															icon: 'trash3-fill',
															label: 'Excluir cargo',
															onClick: () => handleOpenDelete(cargo)
														}
													]}
												/>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
						{total > 0 && (
							<tfoot>
								<tr>
									<th colSpan="4" className="bg-primary text-white rounded-bottom">
										<div className="table-footer d-flex justify-content-between align-items-center flex-wrap gap-1">
											<span>Exibindo {exibindoDe} a {exibindoAte} de {total} registros</span>
											<div>
												<span>Página {page} de {totalPages}</span>
												<button
													type="button"
													className="btn btn-primary btn-sm ms-2 text-white"
													onClick={() => setPage((prev) => prev - 1)}
													disabled={!canPreviousPage}
												>
													<i className="bi bi-caret-left"></i>
												</button>
												<button
													type="button"
													className="btn btn-primary btn-sm ms-1 text-white"
													onClick={() => setPage((prev) => prev + 1)}
													disabled={!canNextPage}
												>
													<i className="bi bi-caret-right"></i>
												</button>
											</div>
										</div>
									</th>
								</tr>
							</tfoot>
						)}
					</table>
				</div>
			)}

			<ModalCargo
				show={showModal}
				onHide={() => setShowModal(false)}
				cargoEditando={cargoEditando}
				dataCursos={dataCursos}
				customStyles={customStyles}
				register={register}
				control={control}
				errors={errors}
				handleSubmit={handleSubmit}
				onSubmit={onSubmit}
				loading={loadingSave}
			/>

			<Modal show={showModalDelete} onHide={() => setShowModalDelete(false)} backdrop="static" centered>
				<Modal.Header closeButton>
					<span className="fw-semibold text-danger">Excluir cargo</span>
				</Modal.Header>
				<Modal.Body>
					Tem certeza que deseja excluir o cargo <strong>{cargoParaDeletar?.nome}</strong>?
					{cargoParaDeletar?.qtd_usuarios > 0 && (
						<p className="text-danger mt-2 mb-0 fs-7">
							Este cargo possui {cargoParaDeletar.qtd_usuarios} usuário(s) vinculado(s) e não poderá ser excluído.
						</p>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn" onClick={() => setShowModalDelete(false)} disabled={loadingDelete}>
						Cancelar
					</button>
					<button
						type="button"
						className="btn btn-danger"
						onClick={handleDelete}
						disabled={loadingDelete || cargoParaDeletar?.qtd_usuarios > 0}
					>
						{loadingDelete ? 'Excluindo...' : 'Excluir'}
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default CargosManager;
